import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Store, CheckCircle, Gauge, TrendingUp, ShieldCheck, MapPin, Target,
  Building, ClipboardList, Star, Minus, Plus, ShoppingCart, Check,
  Sparkles, ArrowRight,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { cartStore } from "@/lib/cart";
import { toast } from "sonner";
import { cn } from "@/lib/utils";


export const Route = createFileRoute("/marketing/local-seo")({
  component: LocalSeoPage,
  head: () => ({
    meta: [
      { title: "Local SEO Services — GMB, Maps, Reviews & Map Pack | CoderLity" },
      { name: "description", content: "Dedicated Local SEO: GMB setup, verification, optimization, ranking and map-pack strategy. Buy reviews, accounts and services by quantity." },
      { property: "og:title", content: "Local SEO Services — CoderLity" },
      { property: "og:description", content: "Rank in the local map pack and own your area." },
      { property: "og:url", content: "/marketing/local-seo" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/local-seo" }],
  }),
});

type Section = {
  id: string;
  icon: typeof Store;
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
};

const SECTIONS: Section[] = [
  {
    id: "gmb-setup",
    icon: Store,
    eyebrow: "Step 1",
    title: "GMB Setup",
    desc: "We create a fully-built Google Business Profile from scratch — accurate categories, NAP, services, products, hours and high-quality media.",
    bullets: [
      "Business profile creation on Google",
      "Correct primary & secondary categories",
      "Logo, cover and 10+ branded photos",
      "Service area & opening hours",
      "Initial Q&A seeded",
    ],
  },
  {
    id: "gmb-verification",
    icon: CheckCircle,
    eyebrow: "Step 2",
    title: "GMB Verification",
    desc: "Postcard, video, phone or live-call verification — we manage the full process and recover stuck profiles.",
    bullets: [
      "Postcard verification handling",
      "Video walk-through verification",
      "Phone & email verification",
      "Bulk verification for chains",
      "Stuck-verification rescue",
    ],
  },
  {
    id: "gmb-optimization",
    icon: Gauge,
    eyebrow: "Step 3",
    title: "GMB Optimization",
    desc: "Every field, attribute, post, product and service tuned for maximum visibility and conversion.",
    bullets: [
      "Keyword-rich business description",
      "Service & product catalog",
      "Weekly Google Posts",
      "Photo geotagging",
      "Schema (LocalBusiness) on website",
    ],
  },
  {
    id: "gmb-ranking",
    icon: TrendingUp,
    eyebrow: "Step 4",
    title: "GMB Ranking",
    desc: "Monthly off-page actions, citations and reviews that move you from page-3 to the top 3 in the local pack.",
    bullets: [
      "Geo-grid rank tracking",
      "Citation building (100+ sources)",
      "Review velocity campaigns",
      "Competitor gap analysis",
      "Monthly performance report",
    ],
  },
  {
    id: "map-pack",
    icon: MapPin,
    eyebrow: "Step 5",
    title: "Map Pack Strategy",
    desc: "Advanced strategy to land and keep all 3 spots in the Google Map Pack for your highest-intent local keywords.",
    bullets: [
      "Map-pack keyword targeting",
      "Proximity & relevance optimization",
      "GBP behavioral signals",
      "Geo-tagged content clusters",
      "Spam fighting & competitor reports",
    ],
  },
];

type Sku = {
  id: string;
  name: string;
  unitLabel: string;
  unitPrice: number;
  step: number;
  min: number;
  max: number;
  defaultQty: number;
  popular?: boolean;
  notes?: string;
};

const SKUS: Sku[] = [
  {
    id: "ls_google_reviews",
    name: "Google Reviews",
    unitLabel: "per review",
    unitPrice: 6,
    step: 5,
    min: 5,
    max: 500,
    defaultQty: 10,
    popular: true,
    notes: "Permanent, geo-relevant, drip-fed over 7–14 days.",
  },
  {
    id: "ls_gmb_account",
    name: "GMB Accounts (Aged / Verified)",
    unitLabel: "per account",
    unitPrice: 49,
    step: 1,
    min: 1,
    max: 50,
    defaultQty: 1,
    notes: "Verified Google Business Profile with transfer of ownership.",
  },
  {
    id: "ls_citations",
    name: "Citation Building",
    unitLabel: "per citation",
    unitPrice: 2,
    step: 25,
    min: 25,
    max: 1000,
    defaultQty: 100,
    notes: "Manual NAP citations on authority directories.",
  },
  {
    id: "ls_gmb_posts",
    name: "GMB Posts",
    unitLabel: "per post",
    unitPrice: 8,
    step: 4,
    min: 4,
    max: 100,
    defaultQty: 8,
    notes: "Branded weekly posts with images, offers and CTAs.",
  },
  {
    id: "ls_gmb_optimization",
    name: "GMB Full Optimization",
    unitLabel: "per profile",
    unitPrice: 149,
    step: 1,
    min: 1,
    max: 20,
    defaultQty: 1,
    notes: "One-time deep optimization of an existing profile.",
  },
  {
    id: "ls_map_pack_seo",
    name: "Map Pack SEO (monthly)",
    unitLabel: "per month",
    unitPrice: 399,
    step: 1,
    min: 1,
    max: 12,
    defaultQty: 3,
    popular: true,
    notes: "Ongoing ranking work to land and hold top-3 map positions.",
  },
];

function LocalSeoPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Local SEO"
        title="Dominate the map pack in your city"
        sub="A 5-step Local SEO system covering GMB setup, verification, optimization, ranking and map-pack strategy — plus pay-per-quantity reviews, accounts and services."
      />

      {/* Section anchors */}
      <section className="border-y border-border/60 bg-card/40 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 py-3 text-xs">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-border bg-background px-3 py-1.5 font-medium text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
              >
                {s.title}
              </a>
            ))}
            <a
              href="#pricing"
              className="rounded-full bg-gradient-brand px-3 py-1.5 font-semibold text-white shadow-glow"
            >
              Build your package
            </a>
          </div>
        </div>
      </section>

      <ServiceGroups />



      {/* 5 strategy sections */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 space-y-16">
          {SECTIONS.map((s, idx) => {
            const Icon = s.icon;
            const flip = idx % 2 === 1;
            return (
              <div
                key={s.id}
                id={s.id}
                className={cn(
                  "grid items-center gap-8 scroll-mt-24 lg:grid-cols-2",
                  flip && "lg:[&>*:first-child]:order-2"
                )}
              >
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-primary">
                    {s.eyebrow}
                  </div>
                  <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-3 text-muted-foreground">{s.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span className="text-foreground/90">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href="#pricing"
                      className="inline-flex items-center gap-1.5 rounded-full bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow"
                    >
                      Configure pricing <Sparkles className="h-3.5 w-3.5" />
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
                    >
                      Talk to a specialist
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 -z-10 rounded-3xl bg-hero-glow opacity-60 blur-2xl" />
                  <div className="rounded-3xl border border-border bg-card p-8">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-glow">
                      <Icon className="h-8 w-8" />
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      {s.bullets.slice(0, 4).map((b) => (
                        <div
                          key={b}
                          className="rounded-xl border border-border/60 bg-background/60 p-3 text-xs text-muted-foreground"
                        >
                          {b}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quantity-based pricing builder */}
      <PricingBuilder />

      {/* Trust strip */}
      <section className="py-12">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="grid gap-6 rounded-2xl border border-border bg-card p-6 sm:grid-cols-3 sm:p-8">
            {[
              { icon: ShieldCheck, title: "TOS-compliant", desc: "Drip-fed delivery, geo-relevant accounts, zero risk to your profile." },
              { icon: Building, title: "Real local citations", desc: "Manual placements on directories Google actually values." },
              { icon: Target, title: "Rank or refund", desc: "If we don't move you in 90 days on Map Pack SEO, your next month is free." },
            ].map((b) => (
              <div key={b.title} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <b.icon className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-semibold">{b.title}</div>
                  <div className="text-xs text-muted-foreground">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </PageShell>
  );
}

function PricingBuilder() {
  const [qtys, setQtys] = useState<Record<string, number>>(
    () => Object.fromEntries(SKUS.map((s) => [s.id, s.defaultQty]))
  );

  const total = useMemo(
    () => SKUS.reduce((sum, s) => sum + s.unitPrice * (qtys[s.id] || 0), 0),
    [qtys]
  );
  const itemCount = useMemo(
    () => SKUS.reduce((sum, s) => sum + (qtys[s.id] > 0 ? 1 : 0), 0),
    [qtys]
  );

  const setQty = (sku: Sku, next: number) => {
    const clamped = Math.max(0, Math.min(sku.max, next));
    setQtys((p) => ({ ...p, [sku.id]: clamped }));
  };

  const addAllToCart = () => {
    let added = 0;
    for (const sku of SKUS) {
      const qty = qtys[sku.id];
      if (qty && qty > 0) {
        cartStore.add({
          id: sku.id,
          name: `Local SEO — ${sku.name}`,
          price: sku.unitPrice,
          qty,
          meta: `${qty} × ${sku.unitLabel}`,
          href: "/marketing/local-seo",
        });
        added++;
      }
    }
    if (added === 0) {
      toast.error("Set a quantity for at least one item.");
    } else {
      toast.success(`Added ${added} item${added > 1 ? "s" : ""} to your cart.`);
    }
  };

  return (
    <section id="pricing" className="scroll-mt-24 py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">
            Build your package
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
            Pay only for what you need
          </h2>
          <p className="mt-3 text-muted-foreground">
            Per-quantity pricing for reviews, accounts and services. Adjust the counts below and add the bundle to your cart.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="grid gap-4 sm:grid-cols-2">
            {SKUS.map((sku) => {
              const qty = qtys[sku.id] || 0;
              const line = sku.unitPrice * qty;
              return (
                <div
                  key={sku.id}
                  className="relative rounded-2xl border border-border bg-card p-5"
                >
                  {sku.popular && (
                    <span className="absolute -top-2 right-4 rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow-glow">
                      Popular
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground">{sku.name}</div>
                      <div className="text-xs text-muted-foreground">{sku.notes}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">${sku.unitPrice}</div>
                      <div className="text-[11px] text-muted-foreground">{sku.unitLabel}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="inline-flex items-center rounded-full border border-border bg-background">
                      <button
                        type="button"
                        onClick={() => setQty(sku, qty - sku.step)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground disabled:opacity-40"
                        disabled={qty <= 0}
                        aria-label={`Decrease ${sku.name}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <input
                        type="number"
                        value={qty}
                        min={0}
                        max={sku.max}
                        onChange={(e) => setQty(sku, Number(e.target.value) || 0)}
                        className="w-14 bg-transparent text-center text-sm font-semibold outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setQty(sku, qty === 0 ? sku.min : qty + sku.step)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground disabled:opacity-40"
                        disabled={qty >= sku.max}
                        aria-label={`Increase ${sku.name}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      ${line.toLocaleString()}
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Min {sku.min} · Max {sku.max} · Step {sku.step}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <aside className="h-fit rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-24">
            <div className="text-sm font-semibold text-foreground">Your package</div>
            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
              {SKUS.filter((s) => (qtys[s.id] || 0) > 0).length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                  Add at least one item to see your total.
                </div>
              ) : (
                SKUS.filter((s) => (qtys[s.id] || 0) > 0).map((s) => {
                  const qty = qtys[s.id];
                  return (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-2 rounded-lg bg-background/60 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {qty} × ${s.unitPrice} {s.unitLabel}
                        </div>
                      </div>
                      <div className="font-semibold">${(qty * s.unitPrice).toLocaleString()}</div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
              <div className="text-xs text-muted-foreground">{itemCount} item{itemCount === 1 ? "" : "s"}</div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="text-2xl font-bold">${total.toLocaleString()}</div>
              </div>
            </div>

            <button
              type="button"
              onClick={addAllToCart}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-white shadow-glow hover:opacity-95"
            >
              <ShoppingCart className="h-4 w-4" /> Add to cart
            </button>
            <Link
              to="/cart"
              className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-border px-4 py-2.5 text-sm font-medium hover:bg-secondary"
            >
              View cart
            </Link>
            <p className="mt-3 text-[11px] text-muted-foreground">
              All quantities are fully adjustable on checkout. Volume discounts available for 50+ reviews or 10+ accounts.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

type GroupItem = { slug: string; title: string };
const GROUPS: { title: string; items: GroupItem[] }[] = [
  {
    title: "Google Business",
    items: [
      { slug: "gmb-setup", title: "GMB Setup" },
      { slug: "gmb-verification", title: "GMB Verification" },
      { slug: "gmb-optimization", title: "GMB Optimization" },
      { slug: "gmb-ranking", title: "GMB Ranking" },
      { slug: "gmb-reinstatement", title: "GMB Reinstatement" },
    ],
  },
  {
    title: "Local Presence",
    items: [
      { slug: "google-maps-seo", title: "Google Maps SEO" },
      { slug: "local-seo", title: "Local SEO" },
      { slug: "citation-building", title: "Citation Building" },
      { slug: "local-listings", title: "Local Listings" },
      { slug: "map-pack-ranking", title: "Map Pack Ranking" },
    ],
  },
  {
    title: "Reputation",
    items: [
      { slug: "google-reviews", title: "Google Reviews" },
      { slug: "reputation-management", title: "Reputation Management" },
    ],
  },
];

function ServiceGroups() {
  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8 space-y-12">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">
            All Local SEO services
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
            Explore every service in detail
          </h2>
          <p className="mt-3 text-muted-foreground">
            Deep-dive pages for every Google Business, Local Presence and Reputation service we offer.
          </p>
        </div>
        {GROUPS.map((g) => (
          <div key={g.title}>
            <h3 className="text-xl font-semibold tracking-tight">{g.title}</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {g.items.map((it) => (
                <Link
                  key={it.slug}
                  to="/marketing/local-seo/$slug"
                  params={{ slug: it.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition"
                >
                  <h4 className="text-base font-semibold">{it.title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                    Explore {it.title.toLowerCase()}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

